/**
 * Simple utility to send notifications via Telegram Bot API.
 */

const BOT_TOKEN = process.env.REACT_APP_TELEGRAM_BOT_TOKEN;
const CHAT_ID = process.env.REACT_APP_TELEGRAM_CHAT_ID;

// Registration specific bot credentials
const REG_BOT_TOKEN = process.env.REACT_APP_REG_TELEGRAM_BOT_TOKEN;
const REG_CHAT_ID = process.env.REACT_APP_REG_TELEGRAM_CHAT_ID;

/**
 * Send a support ticket to a Telegram bot.
 * @param {Object} ticket - Ticket details { subject, message, userEmail }
 * @returns {Promise<boolean>} - Success status
 */
export async function sendSupportTicketToTelegram(ticket) {
  if (!BOT_TOKEN || !CHAT_ID) {
    console.error('Telegram Bot credentials not found in environment variables.');
    return false;
  }

  const { subject, message, userEmail } = ticket;

  // Format the message with basic HTML tags allowed by Telegram
  const text = `
<b>🚨 New Support Ticket: FR-BillNet</b>

<b>👤 From:</b> ${userEmail || 'Unknown User'}
<b>📌 Subject:</b> ${subject}

<b>📝 Message:</b>
${message}

<i>Sent via automated support portal.</i>
  `.trim();

  try {
    const response = await fetch(`https://api.telegram.org/bot${BOT_TOKEN}/sendMessage`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        chat_id: CHAT_ID,
        text: text,
        parse_mode: 'HTML',
      }),
    });

    if (!response.ok) {
      throw new Error(`Telegram API Error: ${response.statusText}`);
    }

    return true;
  } catch (error) {
    console.error('Failed to send Telegram notification:', error);
    return false;
  }
}
/**
 * Send a new user registration alert to a Telegram bot.
 * @param {Object} userData - User details { businessName, email, mobileNo, username }
 * @returns {Promise<boolean>} - Success status
 */
export async function sendNewRegistrationToTelegram(userData) {
  if (!REG_BOT_TOKEN || !REG_CHAT_ID) {
    console.warn('Registration Telegram Bot credentials not found. Falling back to default support bot.');
    // Optional fallback to default bot if registration bot isn't set
    const token = REG_BOT_TOKEN || BOT_TOKEN;
    const chatId = REG_CHAT_ID || CHAT_ID;
    if (!token || !chatId) return false;
    return _sendToTelegram(token, chatId, userData);
  }

  return _sendToTelegram(REG_BOT_TOKEN, REG_CHAT_ID, userData);
}

/**
 * Internal helper to send formatted registration data to Telegram.
 */
async function _sendToTelegram(token, chatId, userData) {
  const { businessName, email, mobileNo, username } = userData;

  const text = `
<b>🚀 New Registration: FR-BillNet</b>

<b>🏢 Business:</b> ${businessName}
<b>👤 Username:</b> ${username}
<b>📧 Email:</b> ${email}
<b>📞 Mobile:</b> ${mobileNo}

<i>Infrastructure access provisioned successfully.</i>
  `.trim();

  try {
    const response = await fetch(`https://api.telegram.org/bot${token}/sendMessage`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        chat_id: chatId,
        text: text,
        parse_mode: 'HTML',
      }),
    });

    return response.ok;
  } catch (error) {
    console.error('Failed to send registration notification:', error);
    return false;
  }
}

